import { ApiError, createApiClient, toApiError } from "@station/api-client";
import { tokenPairSchema } from "@station/contracts";
import { readAppConfig } from "../config.js";
import { authStore } from "./auth-store.js";

const { apiUrl } = readAppConfig();
export const API_URL = apiUrl;

let refreshInFlight: Promise<boolean> | null = null;

/**
 * Sessiz token yenileme — TEK UÇUŞ: aynı anda 401 alan on istek tek bir /auth/refresh çağrısını bekler.
 * (Eşzamanlı iki yenileme, sunucuda "token yeniden kullanımı" şüphesi doğurur.)
 * Başka sekme aynı anda yenilediyse sunucu 409 döner; kısa bir beklemeden sonra güncel çerezle yeniden denenir.
 */
export function refreshSession(): Promise<boolean> {
  refreshInFlight ??= (async () => {
    for (let attempt = 0; attempt < 3; attempt += 1) {
      const response = await fetch(`${apiUrl}/auth/refresh`, {
        method: "POST",
        credentials: "include",
        headers: { "content-type": "application/json", "x-client-kind": "web" },
        body: "{}",
      }).catch(() => null);
      if (response?.ok) {
        authStore.setTokens(tokenPairSchema.parse(await response.json()));
        return true;
      }
      if (response?.status !== 409) break;
      await new Promise((resolve) => setTimeout(resolve, 250 * (attempt + 1)));
    }
    authStore.clear();
    return false;
  })().finally(() => {
    refreshInFlight = null;
  });
  return refreshInFlight;
}

/** 401'de bir kez sessizce yeniler ve isteği tekrarlar. Kimlik uçları (/auth/*) bu döngünün dışındadır. */
const fetchWithRefresh: typeof fetch = async (input, init) => {
  const request = new Request(input, init);
  const retryable = request.clone();
  const response = await fetch(request);
  const isAuthEndpoint = new URL(request.url).pathname.startsWith("/auth/");
  if (response.status !== 401 || isAuthEndpoint) return response;

  // Oturum iptal edildiyse (SESSION_REVOKED) yenileme de başarısız olur ve kullanıcı girişe düşer.
  if (!(await refreshSession())) return response;
  const token = authStore.getAccessToken();
  if (token) retryable.headers.set("authorization", `Bearer ${token}`);
  return fetch(retryable);
};

const MAX_RATE_LIMIT_RETRIES = 2;

/** 429 yanıtından sonra beklenecek süre: sunucunun `Retry-After` değeri (sn, en çok 60) + küçük pay; yoksa 5 sn. */
export function rateLimitDelayMs(response: Pick<Response, "headers">): number {
  const seconds = Number(response.headers.get("retry-after"));
  return (Number.isFinite(seconds) && seconds > 0 ? Math.min(seconds, 60) : 5) * 1000 + 250;
}

/**
 * 429'da sessizce yeniden denenmeyecek yollar: kimlik uçları (giriş sınırı bilinçli bir güvenlik önlemidir) ve İŞ KURALI
 * olan sınırlar — test bildirimi dakikada en çok 3 (`TEST_NOTIFICATION_LIMIT`); kullanıcı "N saniye sonra tekrar deneyin"
 * iletisini HEMEN görmelidir, yoksa düğme 10 sn dönüp aynı hatayı (ya da gecikmiş bir gönderimi) üretir.
 */
export function isRateLimitRetryable(pathname: string): boolean {
  return !pathname.startsWith("/auth/") && pathname !== "/notifications/test";
}

/**
 * Hız sınırına (429) takılan istekler sunucunun bildirdiği süre kadar beklenip yeniden denenir: aksi hâlde tek bir 429
 * (ör. `/me`) sayfayı kalıcı olarak "Yükleniyor"da bırakır, bir sihirbazın önizlemesi sessizce takılır (sorgular 4xx'te
 * yeniden denenmez). YAZMA istekleri için de güvenlidir: API'de hız sınırı koruyucusu (`RateLimitGuard`, global guard)
 * isteği işleyiciye ULAŞMADAN reddeder — 429 alan istek hiç işlenmemiştir, yeniden denemek çift kayıt doğurmaz.
 * İstisnalar `isRateLimitRetryable`'dadır (kullanıcının iletiyi görmesi gereken sınırlar).
 */
const fetchWithRateLimitRetry: typeof fetch = async (input, init) => {
  const template = new Request(input, init);
  const retryable = isRateLimitRetryable(new URL(template.url).pathname);
  for (let attempt = 0; ; attempt += 1) {
    const response = await fetchWithRefresh(template.clone());
    if (response.status !== 429 || !retryable || attempt >= MAX_RATE_LIMIT_RETRIES) return response;
    await new Promise((resolve) => setTimeout(resolve, rateLimitDelayMs(response)));
  }
};

export const api = createApiClient({
  baseUrl: apiUrl,
  clientKind: "web",
  credentials: "include",
  getAccessToken: () => authStore.getAccessToken(),
  fetch: fetchWithRateLimitRetry,
});

export { ApiError, toApiError };
